import axios from 'axios';
import * as Sentry from '@sentry/react';
import { useAuthStore } from '@/store/auth.store';

const baseURL = import.meta.env.VITE_API_BASE_URL || 'https://localhost:44392/api/v1';

export const apiClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// REQUEST: inject Bearer token
apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  (config as any).__startTime = performance.now();
  return config;
});

// Endpoints where a 401 means bad credentials, not an expired session.
// The refresh-and-retry logic must skip these so callers see the 401 verbatim.
// Service paths use relative paths (no /api/v1 prefix) since the base URL
// already includes /api/v1 — so auth paths look like /auth/token, /auth/refresh.
function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return url.includes('/auth/');
}

// RESPONSE: Sentry slow-request tracking + 401 refresh-and-retry
apiClient.interceptors.response.use(
  (response) => {
    const startTime = (response.config as any).__startTime;
    if (startTime) {
      const duration = performance.now() - startTime;
      if (duration > 3000) {
        Sentry.captureMessage(
          `Slow API request: ${response.config.method?.toUpperCase()} ${response.config.url}`,
          {
            level: 'warning',
            tags: { 'request-duration-ms': Math.round(duration).toString() },
            contexts: {
              http: {
                method: response.config.method,
                url: response.config.url,
                duration_ms: duration,
                status: response.status,
              },
            },
          },
        );
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config as typeof error.config & { _retried?: boolean };

    if (
      error.response?.status === 401 &&
      !originalRequest._retried &&
      !isAuthEndpoint(originalRequest?.url)
    ) {
      originalRequest._retried = true;
      try {
        const storedRefresh = localStorage.getItem('agrovet_refresh_token');
        if (!storedRefresh) throw new Error('No refresh token');
        // Raw axios call — bypasses apiClient to avoid re-entering this interceptor.
        // Also avoids importing auth.service (which imports apiClient — circular dep).
        const { data: envelope } = await axios.post(
          `${import.meta.env.VITE_API_BASE_URL}/auth/refresh`,
          { refreshToken: storedRefresh },
          { headers: { 'Content-Type': 'application/json' } },
        );
        const tokens = envelope?.success ? envelope.data : envelope;
        useAuthStore
          .getState()
          .setTokens(tokens.accessToken, tokens.refreshToken, new Date(tokens.accessTokenExpiresAt));
        originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
        return apiClient(originalRequest);
      } catch {
        useAuthStore.getState().clearAuth();
        window.dispatchEvent(new Event('session-expired'));
        window.location.replace('/welcome');
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
